import { useEffect, useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 120;
  background: rgba(2, 6, 23, 0.72);
  display: grid;
  place-items: center;
  padding: 1rem;
`;

const Dialog = styled.section`
  width: min(640px, 100%);
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  background: ${({ theme }) => theme.colors.gray050};
  color: ${({ theme }) => theme.colors.gray900};
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.12rem;
`;

const CloseButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.45rem 0.7rem;
  font-weight: 700;
  cursor: pointer;
`;

const Message = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.92rem;
`;

const UrlInput = styled.textarea`
  width: 100%;
  min-height: 74px;
  resize: vertical;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: 0.88rem;
  line-height: 1.3;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CopyStatus = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.85rem;
`;

type ShareCheckoutLinkModalProps = {
  readonly open: boolean;
  readonly shareUrl: string;
  readonly onClose: () => void;
};

export default function ShareCheckoutLinkModal({
  open,
  shareUrl,
  onClose,
}: ShareCheckoutLinkModalProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (open) {
      setCopyState('idle');
    }
  }, [open, shareUrl]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-label="Share checkout link"
        onClick={(event) => event.stopPropagation()}
      >
        <Header>
          <Title>Share this configuration</Title>
          <CloseButton type="button" onClick={onClose}>
            Close
          </CloseButton>
        </Header>
        <Message>
          This link opens the service page with your current tier, offerings, and add-ons preselected.
        </Message>
        <UrlInput readOnly value={shareUrl} />
        <Actions>
          <ActionButton type="button" onClick={() => void handleCopy()}>
            Copy link
          </ActionButton>
          <ActionButton
            type="button"
            onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}
          >
            Open link
          </ActionButton>
        </Actions>
        <CopyStatus>
          {copyState === 'copied'
            ? 'Link copied to clipboard.'
            : copyState === 'failed'
              ? 'Unable to copy automatically. Copy the URL manually.'
              : 'Share this URL with anyone to send them directly to this configured service page.'}
        </CopyStatus>
      </Dialog>
    </Overlay>
  );
}
